import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { updateMetaTags, addJsonLd } from '../../../utils/seo';
import { supabase, type ArticleCache } from '../../../lib/supabase';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleCache | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState<ArticleCache[]>([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);

      // Récupérer l'article principal
      const { data: articleData, error: articleError } = await supabase
        .from('articles_cache')
        .select('*')
        .eq('slug', slug)
        .single();

      if (articleError) {
        console.error('Erreur lors du chargement de l\'article:', articleError);
        return;
      }

      if (articleData) {
        setArticle(articleData);

        // Récupérer les articles similaires (3 derniers articles)
        const { data: relatedData } = await supabase
          .from('articles_cache')
          .select('*')
          .neq('id', articleData.id)
          .order('published_at', { ascending: false })
          .limit(3);

        if (relatedData) {
          setRelatedArticles(relatedData);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'article:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (article) {
      // Extraire le texte du contenu HTML pour la description
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = article.content || '';
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const excerpt = textContent.substring(0, 160);

      // Extraire les FAQ depuis le contenu HTML
      const extractFAQFromHTML = () => {
        const faqSection = tempDiv.querySelector('h2');
        const faqItems: Array<{ question: string; answer: string }> = [];
        
        if (faqSection && faqSection.textContent?.toLowerCase().includes('question')) {
          let currentElement = faqSection.nextElementSibling;
          
          while (currentElement) {
            if (currentElement.tagName === 'H2') break;
            
            if (currentElement.tagName === 'H3' || currentElement.tagName === 'STRONG' || currentElement.tagName === 'H4') {
              const question = currentElement.textContent?.trim() || '';
              let answer = '';
              let nextEl = currentElement.nextElementSibling;
              
              while (nextEl && !['H2', 'H3', 'H4', 'STRONG'].includes(nextEl.tagName)) {
                if (nextEl.tagName === 'P') {
                  answer += nextEl.textContent?.trim() + ' ';
                }
                nextEl = nextEl.nextElementSibling;
              }
              
              if (question && answer.trim()) {
                faqItems.push({
                  question: question.replace(/^\d+\.\s*/, '').replace(/^[•\-]\s*/, ''),
                  answer: answer.trim()
                });
              }
            }
            
            currentElement = currentElement.nextElementSibling;
          }
        }
        
        return faqItems.length > 0 ? faqItems : null;
      };

      const extractedFAQ = article.faq || extractFAQFromHTML();

      // SEO Meta Tags
      updateMetaTags({
        title: `${article.title} - Vist Magazine`,
        description: excerpt,
        keywords: `${article.title}, vist magazine, article, blog`,
        canonical: `/blog/${article.slug}`,
        ogType: 'article',
        ogImage: article.image_url
      });

      // Base URL
      const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

      // Schema.org JSON-LD for BreadcrumbList
      addJsonLd({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Accueil",
            "item": baseUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": `${baseUrl}/blog`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title,
            "item": `${baseUrl}/blog/${article.slug}`
          }
        ]
      });

      // Schema.org JSON-LD for BlogPosting
      addJsonLd({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": excerpt,
        "image": article.image_url,
        "datePublished": article.published_at,
        "dateModified": article.updated_at || article.published_at,
        "author": {
          "@type": "Person",
          "name": "Jules",
          "jobTitle": "Rédacteur pour Vist Magazine",
          "description": "Il s'intéresse aux transformations culturelles, sociétales et médiatiques à l'ère du numérique."
        },
        "publisher": {
          "@type": "Organization",
          "name": "Vist Magazine",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${baseUrl}/blog/${article.slug}`
        }
      });

      // Schema.org JSON-LD for FAQPage (si FAQ existe)
      if (extractedFAQ && extractedFAQ.length > 0) {
        addJsonLd({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": extractedFAQ.map((faqItem: { question: string; answer: string }) => ({
            "@type": "Question",
            "name": faqItem.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faqItem.answer
            }
          }))
        });
        console.log('✅ Schéma FAQPage ajouté avec', extractedFAQ.length, 'questions');
      }
    }
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
              <div className="hidden md:flex items-center gap-8">
                <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
                <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
                <a href="/blog" className="text-sm text-gray-900 font-medium whitespace-nowrap cursor-pointer">Blog</a>
                <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
                <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
                <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
                <a href="/blog" className="block py-2 text-sm text-gray-900 font-medium">Blog</a>
                <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
                <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
              </div>
            </div>
          )}
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
            <p className="text-gray-500 text-base md:text-lg mt-4">Chargement de l'article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
              <div className="hidden md:flex items-center gap-8">
                <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
                <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
                <a href="/blog" className="text-sm text-gray-900 font-medium whitespace-nowrap cursor-pointer">Blog</a>
                <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
                <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
                <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
                <a href="/blog" className="block py-2 text-sm text-gray-900 font-medium">Blog</a>
                <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
                <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
              </div>
            </div>
          )}
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
            <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
            <a 
              href="/blog" 
              className="inline-block px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              Retour au blog
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Calculer le temps de lecture
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = article.content || '';
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  const wordCount = textContent.split(/\s+/).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  const formattedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '';

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
              <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
              <a href="/blog" className="text-sm text-gray-900 font-medium whitespace-nowrap cursor-pointer">Blog</a>
              <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
              <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
              <a href="/blog" className="block py-2 text-sm text-gray-900 font-medium">Blog</a>
              <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
              <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-2 text-xs md:text-sm overflow-x-auto">
            <a href="/" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Accueil</a>
            <span className="text-gray-400">/</span>
            <a href="/blog" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Blog</a>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium truncate">{article.title}</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <header className="mb-8 md:mb-12">
          <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-gray-500 mb-4 md:mb-6">
            <span>{formattedDate}</span>
            <span>•</span>
            <span>{readTime} min de lecture</span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-base md:text-lg">J</span>
            </div>
            <div>
              <p className="text-xs md:text-sm font-medium text-gray-900">Jules</p>
              <p className="text-xs md:text-sm text-gray-500">Rédacteur pour Vist Magazine</p>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {article.image_url && (
          <div className="relative w-full h-56 md:h-96 rounded-lg overflow-hidden mb-6 md:mb-8">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        )}

        {/* Article Content */}
        <article className="py-8 md:py-[80px] px-4 md:px-8 bg-white">
          <div className="max-w-[900px] mx-auto">
            <div 
              className="prose-article"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* FAQ Section - Enhanced Styling */}
            {article.faq && article.faq.length > 0 && (
              <div className="mt-12 md:mt-16 pt-8 md:pt-12 border-t-2 border-[#D97706]">
                <h2 className="font-serif text-2xl md:text-[36px] text-[#1a1a1a] mb-6 md:mb-8 text-center">
                  Questions fréquentes
                </h2>
                <div className="space-y-3 md:space-y-4">
                  {article.faq.map((faqItem: { question: string; answer: string }, index: number) => (
                    <div 
                      key={index} 
                      className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#D97706] transition-all duration-300"
                    >
                      <button
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-[#fafafa] transition-colors cursor-pointer group"
                      >
                        <h3 className="font-semibold text-base md:text-[18px] text-[#1a1a1a] pr-3 md:pr-4 group-hover:text-[#D97706] transition-colors">
                          {faqItem.question}
                        </h3>
                        <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-[#D97706]/10 group-hover:bg-[#D97706] transition-all">
                          <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-lg md:text-[20px] text-[#D97706] group-hover:text-white transition-colors`}></i>
                        </div>
                      </button>
                      {openFaqIndex === index && (
                        <div className="px-4 md:px-6 pb-4 md:pb-6 animate-fadeIn">
                          <div className="pt-3 md:pt-4 border-t border-gray-100">
                            <p className="text-sm md:text-[16px] text-[#555] leading-relaxed">
                              {faqItem.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Author Bio Section */}
        <div className="mt-8 md:mt-12 p-5 md:p-8 bg-gradient-to-br from-amber-50 via-white to-rose-50 rounded-lg border border-gray-100">
          <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl md:text-2xl">J</span>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">Jules</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 font-medium">Rédacteur pour Vist Magazine</p>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed mb-2 md:mb-3">
                Il s'intéresse aux transformations culturelles, sociétales et médiatiques à l'ère du numérique.
              </p>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed mb-2 md:mb-3">
                À travers ses articles, il analyse les évolutions des modes de vie, des pratiques culturelles et de la circulation de l'information, en privilégiant une approche contextuelle et analytique.
              </p>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                Son travail s'inscrit dans une volonté de proposer des contenus durables, documentés et accessibles, offrant aux lecteurs des clés de compréhension plutôt qu'une réaction à l'actualité immédiate.
              </p>
            </div>
          </div>
        </div>

        {/* Share Section */}
        <div className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-gray-200">
          <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">Partager cet article</p>
          <div className="flex items-center gap-3 md:gap-4">
            <button className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
              <i className="ri-twitter-x-line text-gray-700 text-base md:text-lg"></i>
            </button>
            <button className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
              <i className="ri-facebook-fill text-gray-700 text-base md:text-lg"></i>
            </button>
            <button className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
              <i className="ri-linkedin-fill text-gray-700 text-base md:text-lg"></i>
            </button>
            <button className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
              <i className="ri-mail-line text-gray-700 text-base md:text-lg"></i>
            </button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-gray-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Articles similaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {relatedArticles.map((relatedArticle) => {
                const relatedTempDiv = document.createElement('div');
                relatedTempDiv.innerHTML = relatedArticle.content || '';
                const relatedTextContent = relatedTempDiv.textContent || relatedTempDiv.innerText || '';
                const relatedExcerpt = relatedTextContent.substring(0, 150) + '...';
                const relatedWordCount = relatedTextContent.split(/\s+/).length;
                const relatedReadTime = Math.max(1, Math.ceil(relatedWordCount / 200));
                const relatedDate = relatedArticle.published_at
                  ? new Date(relatedArticle.published_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })
                  : '';

                return (
                  <a
                    key={relatedArticle.id}
                    href={`/blog/${relatedArticle.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-gray-200 transition-all hover:shadow-lg">
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={relatedArticle.image_url || 'https://public.readdy.ai/ai/img_res/0cf39c114a1760eb2c9af26c84d818e5.jpg'}
                          alt={relatedArticle.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5 md:p-6">
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                          <span>{relatedDate}</span>
                          <span>•</span>
                          <span>{relatedReadTime} min</span>
                        </div>
                        <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 leading-tight group-hover:text-amber-600 transition-colors">
                          {relatedArticle.title}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-3">
                          {relatedExcerpt}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
            Restez informé des nouvelles publications
          </h2>
          <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
            Recevez nos derniers articles directement dans votre boîte mail.
          </p>
          <a
            href="/contact"
            className="inline-block px-6 md:px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
          >
            S'abonner à la newsletter
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Vist</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Magazine digital indépendant consacré à l'analyse des tendances culturelles, sociétales et digitales.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Accueil</a></li>
                <li><a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Magazine</a></li>
                <li><a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">À propos</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Thématiques</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-gray-600">Culture &amp; Société</span></li>
                <li><span className="text-sm text-gray-600">Lifestyle</span></li>
                <li><span className="text-sm text-gray-600">Médias digitaux</span></li>
                <li><span className="text-sm text-gray-600">Technologies</span></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Contact</h4>
              <ul className="space-y-2">
                <li><a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Nous contacter</a></li>
                <li><span className="text-sm text-gray-600">Proposer un article</span></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600 text-center md:text-left">
                © {new Date().getFullYear()} Vist Magazine. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
